"use client";
import Link from "next/link";
import React, { memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "./code-block";

function normalizeMarkdownContent(text: string) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    // Keep normal paragraph spacing but avoid exaggerated gaps in lists.
    .replace(/\n{2,}(?=\d+\.\s)/g, "\n")
    .replace(/\n{2,}(?=[*-]\s)/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const components: Partial<Components> = {
  // @ts-expect-error
  code: CodeBlock,
  pre: ({ children }) => <>{children}</>,
  // ✅ TABLE SUPPORT STARTS HERE
  table: ({ children, ...props }) => {
    return (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border border-border text-sm" {...props}>
          {children}
        </table>
      </div>
    );
  },

  thead: ({ children, ...props }) => {
    return (
      <thead className="bg-muted/40" {...props}>
        {children}
      </thead>
    );
  },

  tr: ({ children, ...props }) => {
    return (
      <tr className="border-b border-border" {...props}>
        {children}
      </tr>
    );
  },

  th: ({ children, ...props }) => {
    return (
      <th
        className="px-4 py-2 text-left font-semibold border-r border-border last:border-r-0"
        {...props}
      >
        {children}
      </th>
    );
  },

  td: ({ children, ...props }) => {
    return (
      <td
        className="px-4 py-2 border-r border-border last:border-r-0"
        {...props}
      >
        {children}
      </td>
    );
  },
  // ✅ TABLE SUPPORT ENDS HERE
ul: ({ children, ...props }) => (
  <ul className="list-disc pl-6 mb-2 space-y-0.5" {...props}>
    {children}
  </ul>
),

ol: ({ children, ...props }) => (
  <ol className="list-decimal pl-6 mb-2 space-y-0.5" {...props}>
    {children}
  </ol>
),
  li: ({ node, children, ...props }) => {
    return (
      <li className="py-0.5 [&>p]:mb-0 [&>p]:leading-7" {...props}>
        {children}
      </li>
    );
  },
  strong: ({ node, children, ...props }) => {
    return (
      <span className="font-semibold" {...props}>
        {children}
      </span>
    );
  },
 a: ({ children, ...props }) => (
  <Link
    className="font-medium underline underline-offset-4 hover:text-primary transition-colors"
    target="_blank"
    rel="noreferrer"
    {...props}
  >
    {children}
  </Link>
),
  h1: ({ children, ...props }) => (
  <h1
    className="text-3xl font-bold mt-12 mb-6 text-white"
    {...props}
  >
    {children}
  </h1>
),

h2: ({ children, ...props }) => (
  <h2
    className="text-2xl font-semibold mt-10 mb-4 text-white"
    {...props}
  >
    {children}
  </h2>
),

h3: ({ children, ...props }) => (
  <h3
    className="text-xl font-semibold mt-8 mb-3 text-white/90"
    {...props}
  >
    {children}
  </h3>
),
  h4: ({ node, children, ...props }) => {
    return (
      <h4 className="text-lg font-semibold mt-6 mb-2" {...props}>
        {children}
      </h4>
    );
  },
  h5: ({ node, children, ...props }) => {
    return (
      <h5 className="text-base font-semibold mt-6 mb-2" {...props}>
        {children}
      </h5>
    );
  },
  h6: ({ node, children, ...props }) => {
    return (
      <h6 className="text-sm font-semibold mt-6 mb-2" {...props}>
        {children}
      </h6>
    );
  },
  p: ({ children, ...props }) => (
    <p className="text-[16px] leading-7 mb-2 text-foreground/90" {...props}>
      {children}
    </p>
  ),
};

const remarkPlugins = [remarkGfm];

// const NonMemoizedMarkdown = ({ children }: { children: string }) => {
//   return (
//     <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
//       {children}
//     </ReactMarkdown>
//   );
// };
const NonMemoizedMarkdown = ({ children }: { children: string }) => {
  const normalizedContent = normalizeMarkdownContent(children);
  return (
    // <div className="prose prose-neutral dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        components={components}
      >
        {normalizedContent}
      </ReactMarkdown>
    // </div>
  );
};

export const Markdown = memo(
  NonMemoizedMarkdown,
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);
